import { Button } from '@/components/ui/Button';
import {
  BatchEditionResult,
  EditionOption,
  EditionVisualizeBatchRequest,
  editionsService,
} from '@/services/editions.service';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEditionPresetsStore } from '@/store/edition-presets.store';
import {
  Bookmark,
  Calendar,
  Check,
  CheckSquare,
  FileText,
  FolderOpen,
  Mail,
  Plus,
  Square,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EditionVisualization } from './EditionVisualization';

interface EditionsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  folderUuid: string;
}

interface EditionFormData {
  editions: string[];
  startMonth: string;
  startYear: string;
  endMonth: string;
  endYear: string;
  saveToGed: boolean;
  sendByEmail: boolean;
  emailRecipients: string;
}

const MONTHS = [
  { value: '01', label: 'Janvier' },
  { value: '02', label: 'Février' },
  { value: '03', label: 'Mars' },
  { value: '04', label: 'Avril' },
  { value: '05', label: 'Mai' },
  { value: '06', label: 'Juin' },
  { value: '07', label: 'Juillet' },
  { value: '08', label: 'Août' },
  { value: '09', label: 'Septembre' },
  { value: '10', label: 'Octobre' },
  { value: '11', label: 'Novembre' },
  { value: '12', label: 'Décembre' },
];

const YEARS = Array.from({ length: 10 }, (_, i) => {
  const year = new Date().getFullYear() - 2 + i;
  return { value: year.toString(), label: year.toString() };
});

export function EditionsDrawer({
  isOpen,
  onClose,
  folderUuid,
}: EditionsDrawerProps) {
  const { t } = useTranslation();
  const { presets, addPreset, removePreset } = useEditionPresetsStore();
  const [isAddingPreset, setIsAddingPreset] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const presetInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState<EditionFormData>({
    editions: [],
    startMonth: new Date().getMonth().toString().padStart(2, '0'),
    startYear: new Date().getFullYear().toString(),
    endMonth: new Date().getMonth().toString().padStart(2, '0'),
    endYear: new Date().getFullYear().toString(),
    saveToGed: false,
    sendByEmail: false,
    emailRecipients: '',
  });
  const [isVisualizationOpen, setIsVisualizationOpen] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchEditionResult[]>([]);
  const [batchRequest, setBatchRequest] =
    useState<EditionVisualizeBatchRequest | null>(null);

  // Progress overlay state
  const [isProgressOpen, setIsProgressOpen] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState<string[]>([]);
  const progressTimerRef = useRef<number | null>(null);

  const buildSteps = (
    editionsCount: number,
    monthsCount: number,
    saveToGed: boolean,
    sendByEmail: boolean
  ): string[] => {
    const editionNames = formData.editions
      .map(
        val =>
          editionsData?.data.options.find(
            (opt: EditionOption) => opt.value === val
          )?.name || val
      )
      .slice(0, 3);
    const suffix =
      editionsCount > 3
        ? ` (+${editionsCount - 3})`
        : '';

    const baseSteps = [
      t('editions.progress.connect', 'Connexion au compte OpenPaye'),
      t('editions.progress.openFolder', 'Ouverture du dossier'),
      t('editions.progress.openingEditions', 'Ouverture des éditions'),
      ...editionNames.map((name, i) =>
        t('editions.progress.generateEdition', 'Génération : {{name}}', {
          name,
          current: i + 1,
          total: editionsCount,
        })
      ),
    ];

    if (suffix) {
      baseSteps.push(
        t(
          'editions.progress.generateRemaining',
          'Génération des éditions restantes{{suffix}}',
          { suffix }
        )
      );
    }

    baseSteps.push(
      t(
        'editions.progress.generateSynthesis',
        'Génération des synthèses ({{monthsCount}} mois)',
        { monthsCount }
      )
    );

    if (saveToGed) {
      baseSteps.push(
        t(
          'editions.progress.saveToGed',
          'Sauvegarde des PDFs dans la GED'
        )
      );
    }

    if (sendByEmail) {
      baseSteps.push(
        t(
          'editions.progress.sendByEmail',
          'Envoi des editions par email'
        )
      );
    }

    baseSteps.push(
      t('editions.progress.security', 'Vérifications de sécurité')
    );

    return baseSteps;
  };

  const clearProgressTimer = () => {
    if (progressTimerRef.current) {
      window.clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const startProgress = (
    editionsCount: number,
    monthsCount: number,
    saveToGed: boolean,
    sendByEmail: boolean
  ) => {
    const localSteps = buildSteps(editionsCount, monthsCount, saveToGed, sendByEmail);
    setSteps(localSteps);
    setIsProgressOpen(true);
    setProgressPercent(0);
    setCurrentStepIndex(0);

    // Duration proportional to editions count and months
    const totalMs = Math.min(
      180000,
      Math.max(30000, editionsCount * monthsCount * 5000)
    );
    const tickMs = 500;
    const totalTicks = Math.ceil(totalMs / tickMs);
    let tick = 0;

    clearProgressTimer();
    progressTimerRef.current = window.setInterval(() => {
      tick += 1;
      const percent = Math.min(70, Math.floor((tick / totalTicks) * 70));
      setProgressPercent(percent);

      const stepBoundary = Math.floor(70 / localSteps.length);
      const stepIndex = Math.min(
        localSteps.length - 1,
        Math.floor(percent / stepBoundary)
      );
      setCurrentStepIndex(stepIndex);

      if (tick >= totalTicks) {
        clearProgressTimer();
      }
    }, tickMs);
  };

  const {
    data: editionsData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['editions', 'options', folderUuid],
    queryFn: () => editionsService.getFolderOptions(folderUuid),
    enabled: isOpen && !!folderUuid,
  });

  const batchMutation = useMutation({
    mutationFn: (request: EditionVisualizeBatchRequest) =>
      editionsService.visualizeBatch(folderUuid, request),
    onSuccess: data => {
      setBatchResults(data.data.results);
      setIsVisualizationOpen(true);
      // Speed run the remaining steps
      clearProgressTimer();

      const remainingSteps = steps.length - currentStepIndex - 1;
      if (remainingSteps > 0) {
        const speedRunInterval = setInterval(() => {
          setCurrentStepIndex(prev => {
            const next = prev + 1;
            if (next >= steps.length - 1) {
              clearInterval(speedRunInterval);
              setProgressPercent(100);
              setTimeout(() => {
                setIsProgressOpen(false);
              }, 500);
              return steps.length - 1;
            }
            return next;
          });
        }, 300);
      } else {
        setProgressPercent(100);
        setTimeout(() => {
          setIsProgressOpen(false);
        }, 500);
      }
    },
    onError: () => {
      clearProgressTimer();
      setIsProgressOpen(false);
    },
  });

  const applyPreset = (preset: { id: string; editionValues: string[] }) => {
    const availableValues = editionsData?.data.options.map(
      (opt: EditionOption) => opt.value
    ) || [];
    const validValues = preset.editionValues.filter(v =>
      availableValues.includes(v)
    );
    setFormData(prev => ({ ...prev, editions: validValues }));
    setActivePresetId(preset.id);
  };

  const handleSavePreset = () => {
    const trimmed = presetName.trim();
    if (!trimmed || formData.editions.length === 0) return;
    addPreset(trimmed, formData.editions);
    setPresetName('');
    setIsAddingPreset(false);
  };

  const toggleEdition = (value: string) => {
    setActivePresetId(null);
    setFormData(prev => ({
      ...prev,
      editions: prev.editions.includes(value)
        ? prev.editions.filter(v => v !== value)
        : [...prev.editions, value],
    }));
  };

  const toggleAllEditions = () => {
    if (!editionsData?.data.options) return;
    setActivePresetId(null);
    const allValues = editionsData.data.options.map(
      (opt: EditionOption) => opt.value
    );
    setFormData(prev => ({
      ...prev,
      editions:
        prev.editions.length === allValues.length ? [] : allValues,
    }));
  };

  const handleInputChange = (
    field: 'startMonth' | 'startYear' | 'endMonth' | 'endYear',
    value: string
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleVisualize = () => {
    if (formData.editions.length === 0) return;

    const emailList = formData.sendByEmail
      ? formData.emailRecipients
          .split(',')
          .map(e => e.trim())
          .filter(e => e.length > 0)
      : undefined;

    const request: EditionVisualizeBatchRequest = {
      editionValues: formData.editions,
      startMonth: parseInt(formData.startMonth),
      startYear: parseInt(formData.startYear),
      endMonth: parseInt(formData.endMonth),
      endYear: parseInt(formData.endYear),
      saveToGed: formData.saveToGed,
      sendByEmail: formData.sendByEmail,
      emailRecipients: emailList,
    };

    setBatchRequest(request);

    const monthsCount =
      (request.endYear - request.startYear) * 12 +
      (request.endMonth - request.startMonth) +
      1;
    startProgress(formData.editions.length, monthsCount, formData.saveToGed, formData.sendByEmail);
    batchMutation.mutate(request);
  };

  const handleClose = () => {
    setFormData({
      editions: [],
      startMonth: new Date().getMonth().toString().padStart(2, '0'),
      startYear: new Date().getFullYear().toString(),
      endMonth: new Date().getMonth().toString().padStart(2, '0'),
      endYear: new Date().getFullYear().toString(),
      saveToGed: false,
      sendByEmail: false,
      emailRecipients: '',
    });
    setBatchResults([]);
    setBatchRequest(null);
    setIsVisualizationOpen(false);
    clearProgressTimer();
    setIsProgressOpen(false);
    setProgressPercent(0);
    setCurrentStepIndex(0);
    setActivePresetId(null);
    setIsAddingPreset(false);
    setPresetName('');
    onClose();
  };

  const allSelected =
    editionsData?.data.options &&
    formData.editions.length === editionsData.data.options.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed top-0 right-0 h-full w-xl max-w-xl bg-white shadow-xl transition-transform">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-neutral-200 p-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-neutral-900">
                  {t('editions.title', 'Editions')}
                </h2>
                <p className="text-sm text-neutral-500">
                  {t('editions.description', 'Generer des editions')}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="border-primary-600 mx-auto mb-2 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent"></div>
                  <p className="text-sm text-neutral-600">
                    {t('common.loading', 'Chargement...')}
                  </p>
                </div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <div className="bg-error-100 mx-auto mb-2 flex h-8 w-8 items-center justify-center rounded-full">
                    <X className="text-error-600 h-4 w-4" />
                  </div>
                  <p className="text-sm text-neutral-600">
                    {t(
                      'editions.error',
                      'Erreur lors du chargement des editions'
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Presets / Favoris */}
                {(presets.length > 0 || formData.editions.length > 0) && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Bookmark className="h-4 w-4 text-neutral-400" />
                      <span className="text-sm font-medium text-neutral-900">
                        {t('editions.presets', 'Favoris')}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {presets.map(preset => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => applyPreset(preset)}
                          className={`group flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                            activePresetId === preset.id
                              ? 'border-primary-300 bg-primary-50 text-primary-700'
                              : 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-300 hover:bg-neutral-50'
                          }`}
                        >
                          <Bookmark className="h-3 w-3" />
                          {preset.name}
                          <span
                            role="button"
                            onClick={e => {
                              e.stopPropagation();
                              removePreset(preset.id);
                              if (activePresetId === preset.id) {
                                setActivePresetId(null);
                              }
                            }}
                            className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full text-neutral-400 opacity-0 transition-opacity hover:bg-neutral-200 hover:text-neutral-600 group-hover:opacity-100"
                          >
                            <X className="h-2.5 w-2.5" />
                          </span>
                        </button>
                      ))}

                      {formData.editions.length > 0 && !isAddingPreset && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsAddingPreset(true);
                            setTimeout(() => presetInputRef.current?.focus(), 0);
                          }}
                          className="flex items-center gap-1 rounded-full border border-dashed border-neutral-300 px-3 py-1.5 text-xs font-medium text-neutral-500 transition-colors hover:border-neutral-400 hover:text-neutral-700"
                        >
                          <Plus className="h-3 w-3" />
                          {t(
                            'editions.savePreset',
                            'Sauvegarder la selection'
                          )}
                        </button>
                      )}

                      {isAddingPreset && (
                        <form
                          onSubmit={e => {
                            e.preventDefault();
                            handleSavePreset();
                          }}
                          className="flex items-center gap-1.5"
                        >
                          <input
                            ref={presetInputRef}
                            type="text"
                            value={presetName}
                            onChange={e => setPresetName(e.target.value)}
                            placeholder={t(
                              'editions.presetNamePlaceholder',
                              'Nom du favori...'
                            )}
                            onKeyDown={e => {
                              if (e.key === 'Escape') {
                                setIsAddingPreset(false);
                                setPresetName('');
                              }
                            }}
                            className="focus:border-primary-500 focus:ring-primary-100 w-40 rounded-full border border-neutral-300 px-3 py-1.5 text-xs transition-colors focus:ring-2"
                          />
                          <button
                            type="submit"
                            disabled={!presetName.trim()}
                            className="bg-primary-600 hover:bg-primary-700 flex h-7 w-7 items-center justify-center rounded-full text-white transition-colors disabled:opacity-50"
                          >
                            <Check className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsAddingPreset(false);
                              setPresetName('');
                            }}
                            className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                )}

                {/* Edition Selection - Checkboxes */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-neutral-900">
                      {t('editions.type', "Type d'edition")}
                    </label>
                    <button
                      type="button"
                      onClick={toggleAllEditions}
                      className="text-primary-600 hover:text-primary-700 flex items-center gap-1 text-xs font-medium"
                    >
                      {allSelected ? (
                        <>
                          <CheckSquare className="h-3.5 w-3.5" />
                          {t('common.deselectAll', 'Tout deselectionner')}
                        </>
                      ) : (
                        <>
                          <Square className="h-3.5 w-3.5" />
                          {t('common.selectAll', 'Tout selectionner')}
                        </>
                      )}
                    </button>
                  </div>

                  <div className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-neutral-200 p-2">
                    {editionsData?.data.options.map(
                      (option: EditionOption) => {
                        const isSelected = formData.editions.includes(
                          option.value
                        );
                        return (
                          <label
                            key={option.value}
                            className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                              isSelected
                                ? 'bg-primary-50 text-primary-900'
                                : 'text-neutral-700 hover:bg-neutral-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleEdition(option.value)}
                              className="sr-only"
                            />
                            <span
                              className={`flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border transition-colors ${
                                isSelected
                                  ? 'border-primary-600 bg-primary-600 text-white'
                                  : 'border-neutral-300 bg-white'
                              }`}
                            >
                              {isSelected && (
                                <Check className="h-3 w-3" />
                              )}
                            </span>
                            <span>{option.name}</span>
                          </label>
                        );
                      }
                    )}
                  </div>

                  {formData.editions.length > 0 && (
                    <p className="text-xs text-neutral-500">
                      {t(
                        'editions.selectedCount',
                        '{{count}} edition(s) selectionnee(s)',
                        { count: formData.editions.length }
                      )}
                    </p>
                  )}
                </div>

                {/* Date Range */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-900">
                      {t('editions.dateRange', 'Periode')}
                    </span>
                  </div>

                  {/* Start Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('editions.startDate', 'Date de debut')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={formData.startMonth}
                        onChange={e =>
                          handleInputChange('startMonth', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.startYear}
                        onChange={e =>
                          handleInputChange('startYear', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-neutral-700">
                      {t('editions.endDate', 'Date de fin')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={formData.endMonth}
                        onChange={e =>
                          handleInputChange('endMonth', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {MONTHS.map(month => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <select
                        value={formData.endYear}
                        onChange={e =>
                          handleInputChange('endYear', e.target.value)
                        }
                        className="focus:border-primary-500 focus:ring-primary-100 rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      >
                        {YEARS.map(year => (
                          <option key={year.value} value={year.value}>
                            {year.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Save to GED checkbox */}
                <div className="rounded-lg border border-neutral-200 p-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.saveToGed}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          saveToGed: e.target.checked,
                        }))
                      }
                      className="sr-only"
                    />
                    <span
                      className={`mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border transition-colors ${
                        formData.saveToGed
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-neutral-300 bg-white'
                      }`}
                    >
                      {formData.saveToGed && <Check className="h-3 w-3" />}
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <FolderOpen className="h-4 w-4 text-neutral-500" />
                        <span className="text-sm font-medium text-neutral-900">
                          {t(
                            'editions.saveToGed',
                            'Ajouter les editions a la GED'
                          )}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-500">
                        {t(
                          'editions.saveToGedDescription',
                          'Les editions seront sauvegardees en PDF dans la GED du dossier'
                        )}
                      </p>
                    </div>
                  </label>
                </div>

                {/* Send by Email */}
                <div className="rounded-lg border border-neutral-200 p-4">
                  <label className="flex cursor-pointer items-start gap-3">
                    <input
                      type="checkbox"
                      checked={formData.sendByEmail}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          sendByEmail: e.target.checked,
                        }))
                      }
                      className="sr-only"
                    />
                    <span
                      className={`mt-0.5 flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded border transition-colors ${
                        formData.sendByEmail
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-neutral-300 bg-white'
                      }`}
                    >
                      {formData.sendByEmail && <Check className="h-3 w-3" />}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-neutral-500" />
                        <span className="text-sm font-medium text-neutral-900">
                          {t(
                            'editions.sendByEmail',
                            'Envoyer par email'
                          )}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-neutral-500">
                        {t(
                          'editions.sendByEmailDescription',
                          'Les editions seront envoyees en PDF par email aux destinataires'
                        )}
                      </p>
                    </div>
                  </label>

                  {formData.sendByEmail && (
                    <div className="mt-3 ml-7.5">
                      <input
                        type="text"
                        value={formData.emailRecipients}
                        onChange={e =>
                          setFormData(prev => ({
                            ...prev,
                            emailRecipients: e.target.value,
                          }))
                        }
                        placeholder={t(
                          'editions.emailPlaceholder',
                          'email1@exemple.com, email2@exemple.com'
                        )}
                        className="focus:border-primary-500 focus:ring-primary-100 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm transition-colors focus:ring-2"
                      />
                      <p className="mt-1 text-xs text-neutral-400">
                        {t(
                          'editions.emailHint',
                          'Separez les adresses email par des virgules'
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Summary */}
                {formData.editions.length > 0 && (
                  <div className="rounded-lg bg-neutral-50 p-4">
                    <h4 className="mb-2 text-sm font-medium text-neutral-900">
                      {t('editions.summary', 'Resume')}
                    </h4>
                    <div className="space-y-1 text-sm text-neutral-600">
                      <p>
                        <span className="font-medium">
                          {t('editions.summaryEditions', 'Editions :')}
                        </span>{' '}
                        {formData.editions
                          .map(
                            val =>
                              editionsData?.data.options.find(
                                (opt: EditionOption) => opt.value === val
                              )?.name || val
                          )
                          .join(', ')}
                      </p>
                      <p>
                        <span className="font-medium">
                          {t('editions.summaryPeriod', 'Periode :')}
                        </span>{' '}
                        {
                          MONTHS.find(m => m.value === formData.startMonth)
                            ?.label
                        }{' '}
                        {formData.startYear} -{' '}
                        {
                          MONTHS.find(m => m.value === formData.endMonth)
                            ?.label
                        }{' '}
                        {formData.endYear}
                      </p>
                      {formData.saveToGed && (
                        <p className="text-primary-600 flex items-center gap-1">
                          <FolderOpen className="h-3.5 w-3.5" />
                          {t(
                            'editions.summaryGed',
                            'PDFs sauvegardes dans la GED'
                          )}
                        </p>
                      )}
                      {formData.sendByEmail && formData.emailRecipients.trim() && (
                        <p className="text-primary-600 flex items-center gap-1">
                          <Mail className="h-3.5 w-3.5" />
                          {t(
                            'editions.summaryEmail',
                            'Envoi par email a : {{recipients}}',
                            {
                              recipients: formData.emailRecipients
                                .split(',')
                                .map(e => e.trim())
                                .filter(e => e.length > 0)
                                .join(', '),
                            }
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-neutral-200 p-6">
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
              >
                {t('common.cancel', 'Annuler')}
              </Button>
              <Button
                onClick={handleVisualize}
                disabled={
                  formData.editions.length === 0 ||
                  batchMutation.isPending ||
                  (formData.sendByEmail && formData.emailRecipients.trim().length === 0)
                }
                className="flex-1"
              >
                {batchMutation.isPending
                  ? t('common.loading', 'Chargement...')
                  : t('editions.visualize', 'Visualiser') +
                    (formData.editions.length > 1
                      ? ` (${formData.editions.length})`
                      : '')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Overlay */}
      {isProgressOpen && (
        <div className="fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm" />
          <div className="relative z-10 m-auto w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                  <FileText className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900">
                    {t(
                      'editions.progress.title',
                      'Preparation de vos editions...'
                    )}
                  </h3>
                  <p className="text-sm text-neutral-500">
                    {t(
                      'editions.progress.subtitle',
                      'Cela peut prendre plusieurs secondes selon la periode selectionnee'
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-neutral-100">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <ul className="space-y-2">
              {steps.map((label, index) => {
                const isDone = index < currentStepIndex;
                const isActive = index === currentStepIndex;
                return (
                  <li key={index} className="flex items-center gap-3">
                    <span
                      className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${
                        isDone
                          ? 'bg-success-100 text-success-700'
                          : isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-neutral-100 text-neutral-500'
                      }`}
                    >
                      {isDone ? '✓' : isActive ? '•' : ''}
                    </span>
                    <span
                      className={`text-sm ${
                        isDone
                          ? 'text-neutral-600'
                          : isActive
                            ? 'font-medium text-neutral-900'
                            : 'text-neutral-500'
                      }`}
                    >
                      {label}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="mt-6 text-right">
              <span className="text-xs text-neutral-500">
                {t(
                  'editions.progress.note',
                  'Veuillez ne pas fermer cette fenetre'
                )}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Edition Visualization */}
      {batchResults.length > 0 && (
        <EditionVisualization
          isOpen={isVisualizationOpen}
          onClose={() => {
            setIsVisualizationOpen(false);
            setBatchResults([]);
            setBatchRequest(null);
            clearProgressTimer();
            setIsProgressOpen(false);
          }}
          folderUuid={folderUuid}
          batchResults={batchResults}
          request={batchRequest}
          isLoading={batchMutation.isPending}
          error={batchMutation.error}
        />
      )}
    </div>
  );
}
